import { useState, useEffect } from 'react';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import { inventoryApi, patientsApi, visitsApi } from '@/services/api';
import { useLocations, useIssueStock } from '@/hooks/useQueries';
import type { InventoryItem, Location, IssuedItem, Patient, Visit, LocationStock } from '@/types/index.ts';
import { handleApiError } from '@/utils/errorHandler.ts';
import { getQuantityError } from '@/utils/validation.ts';
import { requiresItemsPerUnit } from '@/types/index.ts';
import BarcodeInput from '@/pages/receive/components/BarcodeInput';
import ScannerModal from '@/pages/receive/components/ScannerModal';
import IssueItemForm from '@/pages/issue/components/IssueItemForm';
import IssueSummary from '@/pages/issue/components/IssueSummary';

export default function IssueStock() {
  const [barcode, setBarcode] = useState('');
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [quantity, setQuantity] = useState<number | ''>(1);
  const [itemsPerUnit, setItemsPerUnit] = useState<number | ''>('');
  const [issuedItems, setIssuedItems] = useState<IssuedItem[]>([]);
  const [notes, setNotes] = useState('');
  // Issue to: another location (transfer) or patient only
  // Default to 'patient' for staff, 'location' for admin
  const [issueToType, setIssueToType] = useState<'location' | 'patient'>('patient');
  const [fromLocationId, setFromLocationId] = useState<number | ''>(''); // Source location
  const [toLocationId, setToLocationId] = useState<number | ''>('');

  // Visit-based patient issuing
  const [visitPatientSearch, setVisitPatientSearch] = useState('');
  const [visitPatientResults, setVisitPatientResults] = useState<Patient[]>([]);
  const [selectedVisitPatient, setSelectedVisitPatient] = useState<Patient | null>(null);
  const [openVisits, setOpenVisits] = useState<Visit[]>([]);
  const [selectedVisitId, setSelectedVisitId] = useState<number | null>(null);
  const [isSearchingVisitPatient, setIsSearchingVisitPatient] = useState(false);
  const [isLoadingOpenVisits, setIsLoadingOpenVisits] = useState(false);
  const [visitPatientNotFound, setVisitPatientNotFound] = useState(false);
  
  // Per-location stock for the selected item
  const [locationStock, setLocationStock] = useState<number | null>(null);
  const [isLoadingLocationStock, setIsLoadingLocationStock] = useState(false);

  // Loading states
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showScanner, setShowScanner] = useState(false);
  const [scannerError] = useState<string | null>(null);
  
  // Use React Query hooks
  const { data: locationsData, isLoading: locationsLoading } = useLocations({ per_page: 100 });
  const { data: toLocationsData } = useLocations({ per_page: 100 });
  const issueStockMutation = useIssueStock();

  // Get current user for role-based UI
  const currentUser = (() => {
    try {
      const userData = localStorage.getItem('userData');
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  })();
  const isAdmin = currentUser?.role === 'admin';

  const [locations, setLocations] = useState<Location[]>([]);
  const [toLocations, setToLocations] = useState<Location[]>([]);
  const loadingData = locationsLoading;

  // Set default issue type based on user role
  useEffect(() => {
    if (!isAdmin && issueToType === 'location') {
      setIssueToType('patient');
    }
  }, [isAdmin]);

  // Focus will be handled by BarcodeInput component

  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Fetch per-location stock whenever the item or source location changes
  useEffect(() => {
    if (!selectedItem || !fromLocationId) {
      setLocationStock(null);
      return;
    }
    const locId = Number(fromLocationId);

    // Try the location_stocks already embedded on the item first
    const embedded: LocationStock[] | undefined = (selectedItem as InventoryItem).location_stocks;
    if (embedded && embedded.length > 0) {
      const match = embedded.find((ls) => ls.location_id === locId);
      setLocationStock(match ? match.quantity : 0);
      return;
    }

    // Fall back: fetch the full item record to get location_stocks
    let cancelled = false;
    const fetchStock = async () => {
      setIsLoadingLocationStock(true);
      try {
        const res = await inventoryApi.getItem(selectedItem.id);
        if (cancelled) return;
        const item = res.data as InventoryItem;
        if (item.location_stocks && item.location_stocks.length > 0) {
          const match = item.location_stocks.find((ls) => ls.location_id === locId);
          setLocationStock(match ? match.quantity : 0);
        } else {
          setLocationStock(item.quantity ?? selectedItem.quantity ?? 0);
        }
      } catch {
        if (!cancelled) setLocationStock(selectedItem.quantity ?? 0);
      } finally {
        if (!cancelled) setIsLoadingLocationStock(false);
      }
    };
    fetchStock();
    return () => { cancelled = true; };
  }, [selectedItem?.id, fromLocationId]);

  // Update locations from React Query data
  useEffect(() => {
    if (locationsData !== undefined) {
      // Handle paginated response: { data: [...], meta: {...} }
      // Or direct array response
      let allLocations: Location[] = [];
      
      if (Array.isArray(locationsData)) {
        allLocations = locationsData;
      } else if (locationsData?.data && Array.isArray(locationsData.data)) {
        allLocations = locationsData.data;
      } else if (locationsData?.data && !Array.isArray(locationsData.data)) {
        // Handle case where data might be wrapped
        allLocations = [];
      }
      
      if (import.meta.env.DEV) {
        console.log('Locations data received:', locationsData);
        console.log('All locations extracted:', allLocations);
        console.log('Number of locations:', allLocations.length);
      }
      
      // Filter out any invalid/null locations
      const issuingLocations = allLocations.filter((loc: Location) => loc && loc.id);
      
      if (import.meta.env.DEV) {
        console.log('Available issuing locations:', issuingLocations);
        console.log('Number of issuing locations:', issuingLocations.length);
      }
      
      setLocations(issuingLocations);
    }
  }, [locationsData]);

  useEffect(() => {
    if (toLocationsData !== undefined) {
      let list: Location[] = [];
      if (Array.isArray(toLocationsData)) list = toLocationsData;
      else if (toLocationsData?.data && Array.isArray(toLocationsData.data)) list = toLocationsData.data;
      setToLocations(list);
    }
  }, [toLocationsData]);

  const handleVisitPatientSearch = async () => {
    const q = visitPatientSearch.trim();
    if (!q) return;
    setIsSearchingVisitPatient(true);
    setVisitPatientResults([]);
    setVisitPatientNotFound(false);
    setSelectedVisitPatient(null);
    setOpenVisits([]);
    setSelectedVisitId(null);
    try {
      const res = (await patientsApi.getPatients({ search: q, per_page: 20 })) as unknown as { data?: Patient[] | { data?: Patient[] } };
      const raw = res?.data;
      const list = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' && 'data' in raw ? (raw as { data?: Patient[] }).data : null) ?? [];
      setVisitPatientResults(list);
      if (list.length === 0) setVisitPatientNotFound(true);
    } catch (err) {
      setError(handleApiError(err, 'Patient search').message);
    } finally {
      setIsSearchingVisitPatient(false);
    }
  };

  const handleSelectVisitPatient = async (patient: Patient) => {
    setSelectedVisitPatient(patient);
    setVisitPatientResults([]);
    setVisitPatientSearch(patient.name);
    setSelectedVisitId(null);
    setIsLoadingOpenVisits(true);
    try {
      const res = (await visitsApi.getVisits({ patient_id: patient.id, status: 'open', per_page: 10 })) as unknown as { data?: Visit[] | { data?: Visit[] } };
      const raw = res?.data;
      const list = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' && 'data' in raw ? (raw as { data?: Visit[] }).data : null) ?? [];
      setOpenVisits(list);
      if (list.length === 1) setSelectedVisitId(list[0].id);
    } catch (err) {
      setError(handleApiError(err, 'Open visits').message);
    } finally {
      setIsLoadingOpenVisits(false);
    }
  };

  const handleBarcodeSearch = async () => {
    if (!barcode.trim()) {
      return;
    }

    // Prevent duplicate searches
    if (isSearching) {
      return;
    }

    try {
      setIsSearching(true);
      setError(null);
      setSuccess(null);

      // Preserve barcode as string to maintain leading/trailing zeros
      const barcodeString = barcode.trim();
      const response = await inventoryApi.searchByBarcode(barcodeString);
      
      // API returns { success: true, data: {...item...}, message: "..." }
      // The handleResponse function returns the entire JSON, so response.data contains the item
      // Check if response.data exists (same as receive page logic)
      if (response.data) {
        const itemData = response.data;
        // Verify we have valid item data with at least an id or name
        if (itemData.id || itemData.name) {
          setSelectedItem(itemData);
          setItemsPerUnit('');
          setError(null);
          setBarcode('');
        } else {
          // Response exists but item data is invalid
          setError('Item data is invalid');
        }
      } else {
        // Response exists but no data
        setError('Item not found');
      }
    } catch (err) {
      const errorDetails = handleApiError(err, 'Barcode Search');
      // Only show error after API call completes
      if (errorDetails.message?.includes('not found') || errorDetails.statusCode === 404 || errorDetails.message?.includes('NotFound')) {
        setError('Item not found');
      } else {
        setError(errorDetails.message);
      }
    } finally {
      setIsSearching(false);
    }
  };

  const handleBarcodeChange = (value: string) => {
    // Only allow numeric input to prevent invalid characters
    const numericValue = value.replace(/\D/g, '');
    setBarcode(numericValue);
    
    // Auto-search when barcode reaches 12 or 13 digits (UPC/EAN-13 standard)
    // Only search if not already searching to avoid duplicate calls
    if ((numericValue.length === 13 || numericValue.length === 12) && !isSearching) {
      setTimeout(() => {
        handleBarcodeSearch();
      }, 100);
    }
  };

  // Handle barcode scanned from scanner modal
  const handleBarcodeScanned = (scannedBarcode: string) => {
    const cleanBarcode = scannedBarcode.trim();
    
    if (cleanBarcode) {
      setShowScanner(false);
      
      const numericBarcode = cleanBarcode.replace(/\D/g, '');
      setBarcode(numericBarcode);
      
      // Auto-search after state update
      setTimeout(() => {
        if (numericBarcode) {
          handleBarcodeSearchWithValue(numericBarcode);
        }
      }, 100);
    }
  };

  // search with a specific barcode value 
  const handleBarcodeSearchWithValue = async (barcodeValue: string) => {
    if (!barcodeValue.trim()) {
      return;
    }

    // Preserve barcode as string to maintain leading/trailing zeros
    const barcodeString = barcodeValue.trim();

    try {
      setIsSearching(true);
      setError(null);
      setSuccess(null);

      const response = await inventoryApi.searchByBarcode(barcodeString);
      
      if (response.data) {
        const itemData = response.data;
        if (itemData.id || itemData.name) {
          setSelectedItem(itemData);
          setItemsPerUnit('');
          setError(null);
          setBarcode('');
        } else {
          setError('Item data is invalid');
        }
      } else {
        setError('Item not found');
      }
    } catch (err) {
      const errorDetails = handleApiError(err, 'Barcode Search');
      if (errorDetails.message?.includes('not found') || errorDetails.statusCode === 404 || errorDetails.message?.includes('NotFound')) {
        setError('Item not found');
      } else {
        setError(errorDetails.message);
      }
    } finally {
      setIsSearching(false);
    }
  };


  const handleAddItem = () => {
    if (!selectedItem) {
      setError('Please select an item');
      return;
    }

    if (!selectedItem.id) {
      setError('Invalid item selected');
      return;
    }

    // Validate location selection
    if (!fromLocationId) {
      setError('Please select the location you are issuing from');
      return;
    }

    // Validate patient visit when issuing to a patient
    if (issueToType === 'patient') {
      if (!selectedVisitPatient) {
        setError('Please search and select a patient before adding items.');
        return;
      }
      if (!selectedVisitId) {
        setError('Please select an active visit for this patient before adding items.');
        return;
      }
    }

    // Validate quantity using centralized validation
    const quantityError = getQuantityError(quantity);
    if (quantityError) {
      setError(quantityError);
      return;
    }

    // Check if unit requires items per unit specification
    const currentUnit = selectedItem.unit || 'pcs';
    const needsItemsPerUnit = requiresItemsPerUnit(currentUnit);
    
    // Validate items per unit if required
    if (needsItemsPerUnit) {
      const itemsPerUnitError = getQuantityError(itemsPerUnit);
      if (itemsPerUnitError) {
        setError(`Please specify how many items are in each ${currentUnit}`);
        return;
      }
    }

    // Use per-location stock when available, otherwise fall back to total quantity
    const available = locationStock !== null ? locationStock : (selectedItem.quantity || 0);

    if (available === 0) {
      setError(`No stock available for this item in the selected location.`);
      return;
    }

    // Normalize quantity values
    const normalizedQty = typeof quantity === 'number' ? quantity : Number(quantity);
    
    // Calculate final quantity: if itemsPerUnit is specified, multiply quantity * itemsPerUnit
    const itemsPerUnitValue = needsItemsPerUnit && itemsPerUnit !== ''
      ? (typeof itemsPerUnit === 'number' ? itemsPerUnit : Number(itemsPerUnit))
      : undefined;
    const finalQuantity = needsItemsPerUnit && itemsPerUnitValue
      ? normalizedQty * itemsPerUnitValue
      : normalizedQty;
    
    // Cannot issue more than available in this location
    if (finalQuantity > available) {
      setError(`Only ${available} ${currentUnit} available in this location`);
      return;
    }

    const newItem: IssuedItem = {
      item_id: selectedItem.id,
      name: selectedItem.name,
      sku: selectedItem.sku,
      barcode: selectedItem.barcode,
      unit: currentUnit,
      quantity: finalQuantity,
      packsBoxes: needsItemsPerUnit ? normalizedQty : undefined,
      itemsPerUnit: itemsPerUnitValue,
      available,
      timestamp: new Date().toLocaleString(),
    };

    setIssuedItems([...issuedItems, newItem]);
    setSelectedItem(null);
    setQuantity(1);
    setItemsPerUnit('');
    setError(null);
  };

  const handleSubmit = async () => {
    if (issuedItems.length === 0) {
      setError('No items to issue');
      return;
    }
    
    // Authorization check: Staff cannot perform location transfers
    if (issueToType === 'location' && !isAdmin) {
      setError('Only administrators can transfer stock between locations.');
      return;
    }
    
    if (issueToType === 'location' && !toLocationId) {
      setError('Please select a destination location.');
      return;
    }
    if (issueToType === 'patient' && !selectedVisitId) {
      setError('Please select a patient visit to issue to.');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      setSuccess(null);

      // Format items for API
      const itemsForApi = issuedItems.map(item => ({
        item_id: item.item_id,
        quantity: item.quantity,
      }));

      // Validate from location is selected
      if (!fromLocationId) {
        setError('Please select the location you are issuing from.');
        return;
      }

      await issueStockMutation.mutateAsync({
        location_id: Number(fromLocationId),
        to_location_id: issueToType === 'location' && toLocationId ? Number(toLocationId) : undefined,
        visit_id: issueToType === 'patient' && selectedVisitId ? selectedVisitId : undefined,
        items: itemsForApi,
        notes: notes.trim() || undefined,
      });

      const toLocationName = toLocations.find(l => l.id === Number(toLocationId))?.name;
      const patientLabel = selectedVisitPatient?.name ?? 'patient';
      const successMsg =
        issueToType === 'location' && toLocationName
          ? `Successfully transferred ${issuedItems.length} item(s) to ${toLocationName}.`
          : issueToType === 'patient'
            ? `Successfully issued ${issuedItems.length} item(s) to ${patientLabel}.`
            : `Successfully issued ${issuedItems.length} item(s).`;
      setSuccess(successMsg);
      setIssuedItems([]);
      setNotes('');
      setBarcode('');
      setIssueToType(isAdmin ? 'location' : 'patient');
      setFromLocationId('');
      setToLocationId('');
      setVisitPatientSearch('');
      setVisitPatientResults([]);
      setSelectedVisitPatient(null);
      setOpenVisits([]);
      setSelectedVisitId(null);
      
      // Auto-dismiss success after 5 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 5000);
    } catch (err) {
      const errorDetails = handleApiError(err, 'Issue Stock');
      setError(errorDetails.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
      <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Issue Stock</h1>
          <div className="bg-[#50b3e1]/10 border border-[#50b3e1]/30 rounded p-2 mt-2 inline-block">
            <div className="flex items-center gap-2">
              <i className="ri-information-line text-[#0997a4] text-sm"></i>
              <p className="text-xs text-gray-700">
                <span className="font-medium">Barcode Scanner Ready:</span> Connect USB scanner or use camera. Click barcode field and scan.
              </p>
            </div>
          </div>
        </div>

        {/* Toast Notifications */}
        {error && (
          <div className="fixed top-4 right-4 z-50 animate-slide-in">
            <div className="flex items-stretch min-w-[280px] max-w-sm rounded-sm shadow-lg overflow-hidden border border-red-200">
              <div className="w-1 bg-red-500 flex-shrink-0" />
              <div className="flex items-center gap-2.5 bg-red-50 px-3 py-2.5 flex-1">
                <i className="ri-error-warning-fill text-red-500 text-base flex-shrink-0" aria-hidden />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-red-800 leading-tight">Error</p>
                  <p className="text-xs text-red-700 mt-0.5 leading-snug">{error}</p>
                </div>
                <button onClick={() => setError(null)} className="text-red-400 hover:text-red-700 flex-shrink-0 ml-1">
                  <i className="ri-close-line text-sm" aria-hidden />
                </button>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="fixed top-4 right-4 z-50 animate-slide-in">
            <div className="flex items-stretch min-w-[280px] max-w-sm rounded-sm shadow-lg overflow-hidden border border-emerald-200">
              <div className="w-1 bg-emerald-500 flex-shrink-0" />
              <div className="flex items-center gap-2.5 bg-emerald-50 px-3 py-2.5 flex-1">
                <i className="ri-checkbox-circle-fill text-emerald-500 text-base flex-shrink-0" aria-hidden />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-emerald-800 leading-tight">Success</p>
                  <p className="text-xs text-emerald-700 mt-0.5 leading-snug">{success}</p>
                </div>
                <button onClick={() => setSuccess(null)} className="text-emerald-400 hover:text-emerald-700 flex-shrink-0 ml-1">
                  <i className="ri-close-line text-sm" aria-hidden />
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Item Details</h3>

            {/* Barcode Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Barcode (EAN-13 / UPC)
                <span className="ml-2 text-xs font-normal text-[#0997a4]">● Scanner Active</span>
              </label>
              <BarcodeInput
                barcode={barcode}
                onBarcodeChange={handleBarcodeChange}
                onSearch={handleBarcodeSearch}
                isSearching={isSearching}
                selectedItem={selectedItem}
                setShowScanner={setShowScanner}
              />
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-gray-500">
                  <i className="ri-flashlight-line"></i> Auto-detects
                </p>
              </div>
            </div>

            {selectedItem && (
              <>
                <IssueItemForm
                  selectedItem={selectedItem}
                  quantity={quantity}
                  onQuantityChange={setQuantity}
                  itemsPerUnit={itemsPerUnit}
                  onItemsPerUnitChange={setItemsPerUnit}
                  locationAvailable={locationStock ?? undefined}
                  onClear={() => {
                    setSelectedItem(null);
                    setQuantity(1);
                    setItemsPerUnit('');
                    setFromLocationId('');
                    setLocationStock(null);
                    setError(null);
                  }}
                />

                {/* From Location Selector */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Issue From Location <span className="text-[#ed1438]">*</span>
                  </label>
                  <select
                    value={fromLocationId}
                    onChange={(e) => setFromLocationId(e.target.value === '' ? '' : Number(e.target.value))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0997a4] cursor-pointer"
                  >
                    <option value="">Select location where stock is available...</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>

                  {/* Inline stock availability for selected location */}
                  {fromLocationId && (
                    <div className="mt-1.5">
                      {isLoadingLocationStock ? (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                          <i className="ri-loader-4-line animate-spin" aria-hidden /> Checking stock…
                        </span>
                      ) : locationStock === 0 ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-sm px-2 py-0.5">
                          <i className="ri-close-circle-fill text-red-500" aria-hidden />
                          No stock in this location
                        </span>
                      ) : locationStock !== null ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-0.5">
                          <i className="ri-checkbox-circle-fill text-emerald-500" aria-hidden />
                          {locationStock} {selectedItem?.unit || 'units'} available in this location
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>

                {/* Issue to: Another location (transfer) or Patient only */}
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Issue to</h3>
                  {isAdmin ? (
                    <p className="text-sm text-gray-500 mb-4">Choose destination: another location (transfer) or a patient.</p>
                  ) : null}
                  <div className="space-y-2">
                    {isAdmin && (
                      <>
                        <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${issueToType === 'location' ? 'border-[#0997a4] bg-[#0997a4]/5' : 'border-gray-200 hover:border-gray-300'}`}>
                          <input
                            type="radio"
                            name="issueTo"
                            checked={issueToType === 'location'}
                            onChange={() => { setIssueToType('location'); setVisitPatientSearch(''); setVisitPatientResults([]); setVisitPatientNotFound(false); setSelectedVisitPatient(null); setOpenVisits([]); setSelectedVisitId(null); }}
                            className="text-[#0997a4]"
                          />
                          <i className="ri-building-line text-gray-500 text-lg" aria-hidden />
                          <span className="text-sm font-medium text-gray-800">Another location (transfer)</span>
                        </label>
                        {issueToType === 'location' && (
                          <div className="ml-9 mt-2">
                            <select
                              value={toLocationId}
                              onChange={(e) => setToLocationId(e.target.value === '' ? '' : Number(e.target.value))}
                              className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            >
                              <option value="">Select destination location</option>
                              {toLocations.filter(l => l.id !== Number(fromLocationId)).map(loc => (
                                <option key={loc.id} value={loc.id}>{loc.name}</option>
                              ))}
                            </select>
                            {!fromLocationId && (
                              <p className="text-xs text-amber-600 mt-1">
                                <i className="ri-alert-line"></i> Please select "From Location" first
                              </p>
                            )}
                          </div>
                        )}
                      </>
                    )}
                    <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${issueToType === 'patient' ? 'border-[#0997a4] bg-[#0997a4]/5' : 'border-gray-200 hover:border-gray-300'}`}>
                      <input
                        type="radio"
                        name="issueTo"
                        checked={issueToType === 'patient'}
                        onChange={() => { setIssueToType('patient'); setToLocationId(''); }}
                        className="text-[#0997a4]"
                      />

                      <i className="ri-user-heart-line text-gray-500 text-lg" aria-hidden />
                      <span className="text-sm font-medium text-gray-800">Patient</span>
                    </label>
                    {issueToType === 'patient' && (
                      <div className="ml-9 mt-3 p-4 rounded-lg bg-[#0997a4]/5 border border-[#0997a4]/20 space-y-3">
                        <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                          <i className="text-[#0997a4]" aria-hidden />
                          Select patient visit
                        </h4>

                        {/* Patient search */}
                        {!selectedVisitPatient ? (
                          <>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={visitPatientSearch}
                                onChange={(e) => { setVisitPatientSearch(e.target.value); setVisitPatientNotFound(false); }}
                                onKeyDown={(e) => e.key === 'Enter' && handleVisitPatientSearch()}
                                placeholder="Search patient by name or MRN"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0997a4]"
                              />
                              <button
                                type="button"
                                onClick={handleVisitPatientSearch}
                                disabled={isSearchingVisitPatient || !visitPatientSearch.trim()}
                                className="px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 transition-colors"
                              >
                                {isSearchingVisitPatient ? '…' : 'Search'}
                              </button>
                            </div>
                            {visitPatientResults.length > 0 && (
                              <ul className="border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden bg-white">
                                {visitPatientResults.map((p) => (
                                  <li key={p.id}>
                                    <button
                                      type="button"
                                      onClick={() => handleSelectVisitPatient(p)}
                                      className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors flex justify-between"
                                    >
                                      <span className="font-medium text-gray-900">{p.name}</span>
                                      {p.identifier && <span className="text-gray-500 text-xs">{p.identifier}</span>}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                            {visitPatientNotFound && (
                              <div className="flex items-center justify-between gap-2 border-l-4 border-l-red-400 border border-red-200 bg-red-50 rounded-sm px-3 py-2">
                                <p className="text-xs font-medium text-red-700">
                                  <span className="font-semibold">"{visitPatientSearch}"</span> doesn't exist. Ensure patient has active visit.
                                </p>
                                <button
                                  type="button"
                                  onClick={() => setVisitPatientNotFound(false)}
                                  className="text-red-400 hover:text-red-600 flex-shrink-0"
                                >
                                  <i className="ri-close-line text-sm"></i>
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="flex items-center gap-2 bg-white border border-[#0997a4]/30 rounded-lg px-3 py-2 text-sm">
                            <i className="ri-user-line text-[#0997a4]" aria-hidden />
                            <span className="font-medium text-[#0997a4]">{selectedVisitPatient.name}</span>
                            {selectedVisitPatient.identifier && <span className="text-gray-500 text-xs">· {selectedVisitPatient.identifier}</span>}
                            <button
                              type="button"
                              onClick={() => { setSelectedVisitPatient(null); setVisitPatientSearch(''); setOpenVisits([]); setSelectedVisitId(null); }}
                              className="ml-auto text-gray-400 hover:text-gray-600"
                            >
                              <i className="ri-close-line" aria-hidden />
                            </button>
                          </div>
                        )}

                        {/* Visit selector */}
                        {selectedVisitPatient && (
                          isLoadingOpenVisits ? (
                            <p className="text-xs text-gray-500">Loading open visits…</p>
                          ) : openVisits.length === 0 ? (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                              <i className="ri-alert-line mr-1" aria-hidden />
                              No open visit for this patient. Please open a visit on the{' '}
                              <a href="/visits" className="underline font-medium">Visits page</a> first.
                            </div>
                          ) : (
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Select visit <span className="text-red-600">*</span>
                              </label>
                              <select
                                value={selectedVisitId ?? ''}
                                onChange={(e) => setSelectedVisitId(e.target.value ? Number(e.target.value) : null)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0997a4]"
                              >
                                <option value="">Select a visit…</option>
                                {openVisits.map((v) => (
                                  <option key={v.id} value={v.id}>
                                    {v.reference_number} · {v.visit_type} · Opened {v.created_at ? new Date(v.created_at).toLocaleDateString() : '—'}
                                  </option>
                                ))}
                              </select>

                            </div>
                          )
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <button
                  onClick={handleAddItem}
                  className="w-full px-4 py-3 bg-[#0997a4] text-white rounded-lg text-sm font-medium hover:bg-[#087d89] transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line mr-2"></i>
                  Add to Issue List
                </button>
              </>
            )}
          </div>

          {/* Issue Summary */}
          {issuedItems.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="text-center py-12">
                <i className="ri-send-plane-line text-6xl text-gray-300 mb-3"></i>
                <p className="text-sm text-gray-500">No items added yet</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <IssueSummary
                items={issuedItems}
                onRemoveItem={(index) => {
                  setIssuedItems(issuedItems.filter((_, i) => i !== index));
                }}
                onSubmit={handleSubmit}
                notes={notes}
                onNotesChange={setNotes}
                isSubmitting={isSubmitting}
                issueToType={issueToType}
                toLocationName={toLocations.find(l => l.id === Number(toLocationId))?.name}
                patientName={selectedVisitPatient?.name ?? ''}
              />
            </div>
          )}
        </div>
      </div>

      {/* Barcode Scanner Modal */}
      <ScannerModal
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleBarcodeScanned}
        error={scannerError}
      />
      </DashboardLayout>
    </>
  );
}
